<template>
    <v-text-field
        ref="input"
        :model-value="asYouTypeInput"
        :density="density"
        :disabled="disabled"
        :label="label && required ? `${label}*` : label"
        variant="outlined"
        :placeholder="placeholder && required ? `${placeholder}*` : placeholder"
        :prepend-inner-icon="prependInnerIcon"
        :required="required"
        :rules="rules"
        :persistent-placeholder="persistentPlaceholder"
        :validate-on="validateOn"
        :hint="hint"
        :hide-details="hideDetails"
        type="tel"
        autocomplete="autocomplete_off_hack_xfr4!k"
        @update:model-value="input"
        @blur="blur"
        @click:control="!asYouTypeInput || !asYouType ? (menu = true) : null"
        @update:focused="focus"
    >
        <template v-slot:prepend-inner>
            <v-menu v-model="menu" location="bottom" height="400">
                <template v-slot:activator="{props}">
                    <div v-bind="props" class="cursor-pointer">
                        <v-icon v-if="!countryCode.length" icon="mdi-earth" start />
                        <span v-else class="fi me-2" :class="`fi-${countryCode[0].toLowerCase()}`" />
                    </div>
                </template>
                <!-- eslint-disable vue/no-v-model-argument -->
                <v-list v-model:selected="countryCode">
                    <v-list-item v-for="country in countries" :key="country.id" :value="country.iso2Code">
                        <template v-slot:prepend>
                            <v-lazy><span class="fi me-2" :class="`fi-${country.iso2Code.toLowerCase()}`" /></v-lazy>
                        </template>
                        <v-lazy>
                            <v-list-item-title>
                                {{ country.name }} <strong>+{{ country.phoneCode }}</strong>
                            </v-list-item-title>
                        </v-lazy>
                    </v-list-item>
                </v-list>
            </v-menu>
        </template>
    </v-text-field>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {AsYouType, getCountryCallingCode, getExampleNumber} from 'libphonenumber-js'
    import examples from 'libphonenumber-js/mobile/examples'

    const moutSuitableCountries = ['AU', 'US']

    @Component
    export class PhoneField extends Vue {
        @VModel() phone
        @Prop({default: false, type: Boolean}) required
        @Prop({default: false, type: Boolean}) hideDetails
        @Prop({default: 'comfortable', type: String}) density
        @Prop({default: false, type: Boolean}) persistentPlaceholder
        @Prop() validateOn
        @Prop({default: false, type: Boolean}) disabled
        @Prop() label
        @Prop() placeholder
        @Prop() prependInnerIcon

        menu = false

        @Emit()
        blur() {}

        @Watch('phone')
        onPhoneChange() {
            if (this.phone) {
                this.input(this.phone)
            } else {
                this.input('')
            }
        }

        asYouType = null
        asYouTypeInput = ''
        _countryCode = null

        mounted() {
            if (this.phone) this.input(this.phone)
        }

        focus(isFocused: boolean) {
            if (isFocused && !this.phone && this.$conf.public.customization.defaultPhoneCountryCode) {
                this.input(this.$conf.public.customization.defaultPhoneCountryCode)
            }
        }

        get rules() {
            return [
                v => !this.required || !!v || `${this.label || this.placeholder} ${this.$t('validation.required')}`,
                v =>
                    !v ||
                    !this.asYouType ||
                    this.asYouType.isValid() ||
                    this.hint ||
                    `${this.label || this.placeholder} ${this.$t('validation.valid')}` +
                        (this.asYouType?.country
                            ? ` ${this.$t('example')}: ${getExampleNumber(this.asYouType.country, examples).number}`
                            : ''),
            ]
        }

        // TODO For best input responsiveness need implement it by mousedown with detect printable keys
        async input(val) {
            this.menu = false
            const asYouType = new AsYouType()
            const asYouTypeInput = asYouType.input(`${val[0] && val[0] !== '+' ? '+' : ''}${val}`)
            const suitableCountries = asYouTypeInput
                ? this.countries.filter(({phoneCode}) => asYouTypeInput.indexOf(`+${phoneCode}`) === 0)
                : []
            if (this._countryCode && suitableCountries.find(({iso2Code}) => iso2Code === this._countryCode)) {
                asYouType.country = this._countryCode
            } else {
                this._countryCode = asYouType.country
                const suitableCountry = suitableCountries.find(({iso2Code}) => moutSuitableCountries.includes(iso2Code))
                if (suitableCountry) asYouType.country = suitableCountry.iso2Code
            }
            if (this.asYouType?.isValid() && !asYouType.isValid() && val.length > asYouTypeInput.length) {
                this.asYouTypeInput = this.asYouTypeInput + ' '
                await this.$nextTick()
                this.asYouTypeInput = this.asYouTypeInput.slice(0, this.asYouTypeInput.length - 1)
            } else {
                this.asYouTypeInput = asYouTypeInput + ' '
                await this.$nextTick()
                this.asYouTypeInput = asYouTypeInput
            }
            this.asYouType = asYouType
            this.phone = this.asYouType.getNumber()?.number || val
        }

        get countryCode() {
            return this.asYouType?.country ? [this.asYouType?.country] : this._countryCode ? [this._countryCode] : []
        }

        set countryCode(val) {
            this.input('').then(() => {
                this._countryCode = val[0]
                this.asYouType = new AsYouType({defaultCountry: val[0]})
                this.input(`+${getCountryCallingCode(val[0])}`)
                this.$refs.input.focus()
            })
        }

        get countries() {
            return runtimeStore.countries
                .filter(country => country.name === 'Greece')
                .reduce((countriesWithPhoneCode, country) => {
                    try {
                        const phoneCode = getCountryCallingCode(country.iso2Code)
                        countriesWithPhoneCode.push({...country, phoneCode})
                    } catch (e) {
                        countriesWithPhoneCode.push({...country, phoneCode: '30'})
                    }
                    return countriesWithPhoneCode
                }, [])
        }

        get hint() {
            const phone = this.$conf.public.customization.phoneNumberHint
            return phone ? this.$t('validation.phone', {phone}) : null
        }
    }

    export default PhoneField
</script>
